import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Drawer,
  Tag,
  message,
  Spin,
  Popconfirm,
  Tabs,
  Collapse,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  LinkOutlined,
  ShopOutlined,
  FileSearchOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../config/api';

const { Title, Text } = Typography;

interface ProblemBusinessRef {
  id: string;
  business_name: string;
}

interface ProblemRow {
  id: string;
  title: string;
  status: string;
  target_date?: string | null;
  owner_first_name?: string | null;
  owner_last_name?: string | null;
  linked_ticket_count?: number;
  root_cause?: string | null;
  businesses?: ProblemBusinessRef[] | null;
}

interface ProactiveTicket {
  ticket_number: string;
  subject?: string | null;
  status: string;
  priority: string;
  created_at: string;
}

interface FrequentBusinessRow {
  business_id: string;
  business_name: string;
  distinct_days: number;
  ticket_count: number;
  tickets: ProactiveTicket[];
}

interface SubjectClusterRow {
  business_id: string;
  business_name: string;
  normalized_subject: string;
  sample_subject: string;
  ticket_count: number;
  tickets: ProactiveTicket[];
}

interface CfClusterRow {
  business_id: string;
  business_name: string;
  field_key: string;
  field_label: string;
  issue_value: string;
  sample_subject: string;
  ticket_count: number;
  tickets: ProactiveTicket[];
}

type CreateAttachPayload = {
  ticket_numbers?: string[];
  signature_field_key?: string;
  issue_value?: string;
};

interface VolumeCategoryRow {
  category_id: string;
  category_name: string;
  ticket_count: number;
}

interface SemanticClusterRow {
  business_id: string;
  business_name: string;
  ticket_count: number;
  min_similarity_guess: number;
  exemplar_subject?: string | null;
  exemplar_ticket_number: string;
  tickets: (ProactiveTicket & { id?: string })[];
}

/** Row shape from GET /api/businesses/search (`Business.search` returns full business columns). */
interface BusinessSearchRow {
  id: string;
  business_name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  mid?: string | null;
  merchant_id?: string | null;
  store_id?: string | null;
}

function businessSearchOptionLabel(b: BusinessSearchRow) {
  const address = [b.address, b.city, b.state, b.zip_code].filter(Boolean).join(', ');
  const mid = (b.mid || b.merchant_id || '').trim() || '—';
  const storeId =
    b.store_id != null && String(b.store_id).trim() !== '' ? String(b.store_id).trim() : '—';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
        maxWidth: 480,
        padding: '4px 0',
      }}
    >
      <Text strong style={{ fontSize: 13, lineHeight: 1.3 }}>
        {b.business_name}
      </Text>
      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.35 }}>
        {address || '—'}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        MID: {mid}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Store ID: {storeId}
      </Text>
    </div>
  );
}

function makeBusinessOptionRender(rows: BusinessSearchRow[]) {
  return (optOrPayload: any) => {
    const opt = optOrPayload?.option ?? optOrPayload;
    const id = opt?.value as string | undefined;
    const b = id ? rows.find((x) => x.id === id) : undefined;
    if (!b) return <span>{(opt?.label as string) ?? ''}</span>;
    return businessSearchOptionLabel(b);
  };
}

const Problems: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [linkTicketOpen, setLinkTicketOpen] = useState(false);
  const [businessSearch, setBusinessSearch] = useState('');
  const [extraBusinessChoices, setExtraBusinessChoices] = useState<{ id: string; business_name: string }[]>([]);
  const [form] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [repeatSubjectSearch, setRepeatSubjectSearch] = useState('');
  const [repeatCfSearch, setRepeatCfSearch] = useState('');
  const [subjectClusterDrawer, setSubjectClusterDrawer] = useState<SubjectClusterRow | null>(null);
  const [cfClusterDrawer, setCfClusterDrawer] = useState<CfClusterRow | null>(null);
  const [createAttach, setCreateAttach] = useState<CreateAttachPayload>({});
  const [subjectRepeatGrouped, setSubjectRepeatGrouped] = useState(false);

  const businessPickerActive = createOpen || !!detailId;

  const { data: businessesInitial } = useQuery(
    ['problems-business-picker-initial'],
    () => api.get('/api/businesses/search?search=&limit=200').then((res) => res.data.businesses || []),
    { enabled: businessPickerActive, staleTime: 5 * 60 * 1000 }
  );

  const { data: businessesSearch = [], isFetching: businessesSearchLoading } = useQuery(
    ['problems-business-picker-search', businessSearch],
    () =>
      api
        .get(`/api/businesses/search?search=${encodeURIComponent(businessSearch)}&limit=200`)
        .then((res) => res.data.businesses || []),
    { enabled: businessPickerActive && businessSearch.length >= 1, staleTime: 30000 }
  );

  const businessSelectOptions = businessSearch.length >= 1 ? businessesSearch : businessesInitial || [];

  const mergedBusinessOptions = useMemo(() => {
    const base = (businessSelectOptions || []) as BusinessSearchRow[];
    const seen = new Set(base.map((b) => b.id));
    const extras = extraBusinessChoices.filter((e) => e.id && !seen.has(e.id)) as BusinessSearchRow[];
    return [...extras, ...base];
  }, [businessSelectOptions, extraBusinessChoices]);

  useEffect(() => {
    if (!createOpen && !detailId) setBusinessSearch('');
  }, [createOpen, detailId]);

  const { data: listData, isLoading: listLoading } = useQuery(
    ['problems-list'],
    async () => (await api.get('/api/problems')).data
  );

  const { data: detailData, isLoading: detailLoading } = useQuery(
    ['problem-detail', detailId],
    async () => (await api.get(`/api/problems/${detailId}`)).data,
    { enabled: !!detailId }
  );

  const detailBusinessExtras = useMemo(() => {
    const list = detailData?.problem?.businesses;
    if (!Array.isArray(list)) return [];
    return list.map((x: ProblemBusinessRef) => ({ id: x.id, business_name: x.business_name }));
  }, [detailData?.problem?.businesses]);

  const mergedBusinessOptionsDetail = useMemo(() => {
    const base = (businessSelectOptions || []) as BusinessSearchRow[];
    const seen = new Set(base.map((b) => b.id));
    const extras = detailBusinessExtras.filter((e) => e.id && !seen.has(e.id)) as BusinessSearchRow[];
    return [...extras, ...base];
  }, [businessSelectOptions, detailBusinessExtras]);

  const renderCreateBusinessOption = useMemo(
    () => makeBusinessOptionRender(mergedBusinessOptions as BusinessSearchRow[]),
    [mergedBusinessOptions]
  );

  const renderDetailBusinessOption = useMemo(
    () => makeBusinessOptionRender(mergedBusinessOptionsDetail as BusinessSearchRow[]),
    [mergedBusinessOptionsDetail]
  );

  const { data: assignable } = useQuery(
    ['users-assignable-problems'],
    async () => (await api.get('/api/users/assignable')).data
  );

  const { data: proactiveData, isLoading: proactiveLoading, refetch: refetchProactive } = useQuery(
    ['problems-proactive-patterns'],
    async () =>
      (
        await api.get('/api/problems/proactive-patterns', {
          params: {
            days: 14,
            minDistinctDays: 3,
            subjectClusterMin: 2,
            maxClustersPerBusiness: 25,
            ticketsPerSubjectCluster: 100,
          },
        })
      ).data,
    { staleTime: 60000 }
  );

  const { data: cfClusterData, isLoading: cfClusterLoading, refetch: refetchCfClusters } = useQuery(
    ['problems-repeat-cf-clusters'],
    async () =>
      (
        await api.get('/api/problems/repeat-custom-field-clusters', {
          params: {
            days: 14,
            minClusterSize: 2,
            maxClustersPerBusiness: 25,
            ticketsPerCluster: 100,
          },
        })
      ).data,
    { staleTime: 60000 }
  );

  const { data: volumeSpikesData, isLoading: volumeSpikesLoading, refetch: refetchVolumeSpikes } = useQuery(
    ['problems-volume-spikes'],
    async () => (await api.get('/api/problems/volume-spikes', { params: { days: 14 } })).data,
    { staleTime: 120000 }
  );

  const { data: semanticData, isLoading: semanticLoading, refetch: refetchSemantic } = useQuery(
    ['problems-semantic-patterns'],
    async () =>
      (
        await api.get('/api/problems/semantic-patterns', {
          params: {
            days: 21,
            minClusterSize: 2,
            minSimilarity: 0.82,
            maxBusinesses: 25,
            maxClusters: 45,
          },
        })
      ).data,
    { staleTime: 120000 }
  );

  const createMutation = useMutation(
    async ({
      values,
      attach = {},
    }: {
      values: Record<string, unknown>;
      attach?: CreateAttachPayload;
    }) => {
      const ids = Array.isArray(values.business_ids) ? (values.business_ids as string[]) : [];
      const payload: Record<string, unknown> = {
        ...values,
        business_ids: ids,
        target_date: values.target_date ? (values.target_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
      };
      delete (payload as { business_id?: unknown }).business_id;
      if (attach.ticket_numbers?.length) {
        payload.ticket_numbers = attach.ticket_numbers;
      }
      if (attach.signature_field_key) {
        payload.signature_field_key = attach.signature_field_key;
      }
      if (attach.issue_value != null && attach.issue_value !== '') {
        payload.issue_value = attach.issue_value;
      }
      if (payload.signature_field_key === '') delete payload.signature_field_key;
      if (payload.issue_value === '') delete payload.issue_value;
      if (payload.category_id === '') delete payload.category_id;
      await api.post('/api/problems', payload);
    },
    {
      onSuccess: () => {
        message.success('Problem created');
        queryClient.invalidateQueries(['problems-list']);
        setCreateOpen(false);
        setCreateAttach({});
        form.resetFields();
      },
      onError: () => {
        message.error('Failed to create problem');
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, values }: { id: string; values: Record<string, unknown> }) => {
      await api.patch(`/api/problems/${id}`, values);
    },
    {
      onSuccess: () => {
        message.success('Updated');
        queryClient.invalidateQueries(['problems-list']);
        queryClient.invalidateQueries(['problem-detail', detailId]);
      },
      onError: () => {
        message.error('Update failed');
      },
    }
  );

  const linkMutation = useMutation(
    async ({ problemId, ticketNumber }: { problemId: string; ticketNumber: string }) => {
      const t = await api.get(`/api/tickets/number/${encodeURIComponent(ticketNumber.trim())}`);
      const ticketId = t.data?.id;
      if (!ticketId) throw new Error('Ticket not found');
      await api.post(`/api/problems/${problemId}/tickets`, { ticket_id: ticketId });
    },
    {
      onSuccess: () => {
        message.success('Ticket linked');
        queryClient.invalidateQueries(['problem-detail', detailId]);
        queryClient.invalidateQueries(['problems-list']);
        setLinkTicketOpen(false);
        linkForm.resetFields();
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } };
        message.error(err?.response?.data?.error || 'Could not link ticket');
      },
    }
  );

  const unlinkMutation = useMutation(
    async ({ problemId, ticketId }: { problemId: string; ticketId: string }) => {
      await api.delete(`/api/problems/${problemId}/tickets/${ticketId}`);
    },
    {
      onSuccess: () => {
        message.success('Unlinked');
        queryClient.invalidateQueries(['problem-detail', detailId]);
        queryClient.invalidateQueries(['problems-list']);
      },
    }
  );

  const deleteProblemMutation = useMutation(
    async (id: string) => {
      await api.delete(`/api/problems/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Problem deleted');
        setDetailId(null);
        queryClient.invalidateQueries(['problems-list']);
      },
      onError: () => {
        message.error('Failed to delete problem');
      },
    }
  );

  const problems: ProblemRow[] = listData?.problems || [];

  /** Subject clusters from API, grouped by business for Repeat subjects UI. */
  const subjectClustersByBusiness = useMemo(() => {
    const clusters = (proactiveData?.subjectClusters as SubjectClusterRow[]) || [];
    const map = new Map<
      string,
      { business_id: string; business_name: string; clusters: SubjectClusterRow[] }
    >();
    for (const row of clusters) {
      if (!row.business_id) continue;
      let g = map.get(row.business_id);
      if (!g) {
        g = {
          business_id: row.business_id,
          business_name: row.business_name || 'Business',
          clusters: [],
        };
        map.set(row.business_id, g);
      }
      g.clusters.push(row);
    }
    const list = Array.from(map.values());
    list.sort((a, b) =>
      (a.business_name || '').localeCompare(b.business_name || '', undefined, { sensitivity: 'base' })
    );
    for (const g of list) {
      g.clusters.sort((a, b) => b.ticket_count - a.ticket_count);
    }
    return list;
  }, [proactiveData?.subjectClusters]);

  const subjectClusterFlatRows = useMemo(
    () => (proactiveData?.subjectClusters as SubjectClusterRow[]) || [],
    [proactiveData?.subjectClusters]
  );

  const filteredSubjectClusterRows = useMemo(() => {
    const q = repeatSubjectSearch.trim().toLowerCase();
    if (!q) return subjectClusterFlatRows;
    return subjectClusterFlatRows.filter(
      (r) =>
        (r.business_name || '').toLowerCase().includes(q) ||
        (r.normalized_subject || '').toLowerCase().includes(q) ||
        (r.sample_subject || '').toLowerCase().includes(q)
    );
  }, [subjectClusterFlatRows, repeatSubjectSearch]);

  const cfClusterFlatRows = useMemo(
    () => (cfClusterData?.clusters as CfClusterRow[]) || [],
    [cfClusterData?.clusters]
  );

  const filteredCfClusterRows = useMemo(() => {
    const q = repeatCfSearch.trim().toLowerCase();
    if (!q) return cfClusterFlatRows;
    return cfClusterFlatRows.filter(
      (r) =>
        (r.business_name || '').toLowerCase().includes(q) ||
        (r.field_key || '').toLowerCase().includes(q) ||
        (r.field_label || '').toLowerCase().includes(q) ||
        (r.issue_value || '').toLowerCase().includes(q) ||
        (r.sample_subject || '').toLowerCase().includes(q)
    );
  }, [cfClusterFlatRows, repeatCfSearch]);

  const semanticClustersByBusiness = useMemo(() => {
    const clusters = (semanticData?.clusters as SemanticClusterRow[]) || [];
    const map = new Map<
      string,
      { business_id: string; business_name: string; clusters: SemanticClusterRow[] }
    >();
    for (const row of clusters) {
      if (!row.business_id) continue;
      let g = map.get(row.business_id);
      if (!g) {
        g = {
          business_id: row.business_id,
          business_name: row.business_name || 'Business',
          clusters: [],
        };
        map.set(row.business_id, g);
      }
      g.clusters.push(row);
    }
    const list = Array.from(map.values());
    list.sort((a, b) =>
      (a.business_name || '').localeCompare(b.business_name || '', undefined, { sensitivity: 'base' })
    );
    for (const g of list) {
      g.clusters.sort((a, b) => b.ticket_count - a.ticket_count);
    }
    return list;
  }, [semanticData?.clusters]);

  const problemTicketsTab = (
    <Card>
      {listLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          size="small"
          rowKey="id"
          dataSource={problems}
          onRow={(record) => ({
            onClick: () => setDetailId(record.id),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true },
            {
              title: 'Businesses',
              key: 'businesses',
              width: 260,
              render: (_: unknown, r: ProblemRow) => {
                const list = r.businesses;
                if (!list || list.length === 0) return '—';
                return (
                  <Space size={[4, 4]} wrap onClick={(e) => e.stopPropagation()}>
                    {list.map((b) => (
                      <Button
                        key={b.id}
                        type="link"
                        size="small"
                        style={{ padding: 0, height: 'auto' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/businesses/${b.id}`);
                        }}
                      >
                        {b.business_name}
                      </Button>
                    ))}
                  </Space>
                );
              },
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 120,
              render: (s: string) => {
                const c = s === 'resolved' ? 'green' : s === 'in_progress' ? 'blue' : 'default';
                return <Tag color={c}>{s.replace('_', ' ')}</Tag>;
              },
            },
            {
              title: 'Owner',
              key: 'owner',
              width: 160,
              render: (_: unknown, r: ProblemRow) =>
                r.owner_first_name || r.owner_last_name
                  ? `${r.owner_first_name || ''} ${r.owner_last_name || ''}`.trim()
                  : '—',
            },
            {
              title: 'Target',
              dataIndex: 'target_date',
              key: 'target_date',
              width: 120,
              render: (d: string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '—'),
            },
            {
              title: 'Tickets',
              dataIndex: 'linked_ticket_count',
              key: 'linked_ticket_count',
              width: 80,
            },
          ]}
          pagination={{ pageSize: 15 }}
        />
      )}
    </Card>
  );

  const proactiveTicketTableColumns = useMemo(
    () => [
      {
        title: 'Ticket',
        dataIndex: 'ticket_number',
        key: 'ticket_number',
        width: 110,
        render: (n: string) => (
          <Button type="link" size="small" onClick={() => navigate(`/workspace/number/${n}`)}>
            #{n}
          </Button>
        ),
      },
      { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: (s: string) => <Tag>{s}</Tag>,
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 150,
        render: (d: string) => dayjs(d).format('MMM D, YYYY HH:mm'),
      },
    ],
    [navigate]
  );

  const openCreateFromSubjectCluster = (row: SubjectClusterRow) => {
    const nums = row.tickets.map((t) => String(t.ticket_number || '').trim()).filter(Boolean);
    setCreateAttach({ ticket_numbers: nums });
    form.setFieldsValue({
      title: `Problem: ${row.normalized_subject} @ ${row.business_name}`.slice(0, 500),
      business_ids: [row.business_id],
      description:
        `Repeat subject keyword "${row.normalized_subject}". Sample: ${row.sample_subject || '—'}. ` +
        `Tickets (${nums.length}): ${nums.slice(0, 40).map((n) => `#${n}`).join(', ')}${nums.length > 40 ? ' …' : ''}`,
      signature_field_key: '',
      category_id: '',
      issue_value: '',
    });
    setExtraBusinessChoices([{ id: row.business_id, business_name: row.business_name }]);
    setBusinessSearch('');
    setSubjectClusterDrawer(null);
    setCreateOpen(true);
  };

  const openCreateFromCfCluster = (row: CfClusterRow) => {
    const nums = row.tickets.map((t) => String(t.ticket_number || '').trim()).filter(Boolean);
    setCreateAttach({
      ticket_numbers: nums,
      signature_field_key: row.field_key,
      issue_value: row.issue_value,
    });
    form.setFieldsValue({
      title: `Problem: ${row.field_label || row.field_key} = ${row.issue_value} @ ${row.business_name}`.slice(0, 500),
      business_ids: [row.business_id],
      description:
        `Repeat custom field "${row.field_label || row.field_key}" value "${row.issue_value}". ` +
        `Tickets (${nums.length}): ${nums.slice(0, 40).map((n) => `#${n}`).join(', ')}${nums.length > 40 ? ' …' : ''}`,
      signature_field_key: row.field_key,
      issue_value: row.issue_value,
    });
    setExtraBusinessChoices([{ id: row.business_id, business_name: row.business_name }]);
    setBusinessSearch('');
    setCfClusterDrawer(null);
    setCreateOpen(true);
  };

  const patternsTab = (
    <>
      <Card
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <ShopOutlined />
            <span>Repeated contact (by business)</span>
          </Space>
        }
        extra={
          <Button size="small" onClick={() => refetchProactive()}>
            Refresh
          </Button>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Businesses with new tickets on at least 3 different calendar days in the last 14 days (scoped to tickets you
          can see). Expand a row for recent tickets in that window.
        </Text>
        {proactiveLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : !(proactiveData?.frequentBusinesses as FrequentBusinessRow[] | undefined)?.length ? (
          <Text type="secondary">No businesses meet this pattern right now.</Text>
        ) : (
          <Table<FrequentBusinessRow>
            size="small"
            rowKey="business_id"
            pagination={false}
            dataSource={proactiveData.frequentBusinesses as FrequentBusinessRow[]}
            expandable={{
              expandedRowRender: (row) => (
                <Table<ProactiveTicket>
                  size="small"
                  rowKey="ticket_number"
                  dataSource={row.tickets}
                  pagination={false}
                  columns={[
                    {
                      title: 'Ticket',
                      dataIndex: 'ticket_number',
                      key: 'ticket_number',
                      width: 110,
                      render: (n: string) => (
                        <Button type="link" size="small" onClick={() => navigate(`/workspace/number/${n}`)}>
                          #{n}
                        </Button>
                      ),
                    },
                    { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      width: 90,
                      render: (s: string) => <Tag>{s}</Tag>,
                    },
                    {
                      title: 'Priority',
                      dataIndex: 'priority',
                      key: 'priority',
                      width: 90,
                    },
                    {
                      title: 'Created',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      width: 150,
                      render: (d: string) => dayjs(d).format('MMM D, YYYY HH:mm'),
                    },
                  ]}
                />
              ),
            }}
            columns={[
              { title: 'Business', dataIndex: 'business_name', key: 'business_name', ellipsis: true },
              {
                title: 'Distinct days',
                dataIndex: 'distinct_days',
                key: 'distinct_days',
                width: 110,
              },
              { title: 'Tickets in window', dataIndex: 'ticket_count', key: 'ticket_count', width: 130 },
            ]}
          />
        )}
      </Card>

      <Card
        title={
          <Space>
            <FileSearchOutlined />
            <span>Repeat subjects (same business)</span>
          </Space>
        }
        extra={
          <Space wrap>
            <Space size={6} align="center">
              <Text type="secondary" style={{ fontSize: 12 }}>
                Grouped view
              </Text>
              <Switch checked={subjectRepeatGrouped} onChange={setSubjectRepeatGrouped} size="small" />
            </Space>
            <Input.Search
              allowClear
              placeholder="Filter business / keyword / sample"
              onChange={(e) => setRepeatSubjectSearch(e.target.value)}
              style={{ width: 260 }}
            />
            <Button size="small" onClick={() => refetchProactive()}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Each cluster is a <strong>keyword</strong> (from subject wording or two words joined). Turn on <Text strong>
            Grouped view
          </Text>{' '}
          for the previous per-business layout. Default is a flat sortable table—use <Text strong>View tickets</Text>{' '}
          for the drawer, then <Text strong>New problem</Text> to create a problem and bulk-link those ticket numbers.
          At least 2 tickets in the last 14 days. Scoped to tickets you can see.
        </Text>
        {proactiveLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : subjectClusterFlatRows.length === 0 ? (
          <Text type="secondary">No repeat subject clusters in this window.</Text>
        ) : subjectRepeatGrouped ? (
          <Collapse
            bordered={false}
            style={{ background: 'transparent' }}
            items={subjectClustersByBusiness.map((grp) => ({
              key: grp.business_id,
              label: (
                <Space wrap>
                  <Text strong>{grp.business_name}</Text>
                  <Tag>
                    {grp.clusters.length} subject pattern{grp.clusters.length === 1 ? '' : 's'}
                  </Tag>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/businesses/${grp.business_id}`);
                    }}
                  >
                    Open business
                  </Button>
                </Space>
              ),
              children: (
                <Table<SubjectClusterRow>
                  size="small"
                  rowKey={(r) => `${grp.business_id}-${r.normalized_subject}`}
                  pagination={false}
                  dataSource={grp.clusters}
                  expandable={{
                    expandedRowRender: (row) => (
                      <Table<ProactiveTicket>
                        size="small"
                        rowKey="ticket_number"
                        dataSource={row.tickets}
                        pagination={false}
                        columns={proactiveTicketTableColumns}
                      />
                    ),
                  }}
                  columns={[
                    {
                      title: 'Keyword / sample',
                      key: 'kw_sample',
                      ellipsis: true,
                      render: (_: unknown, row: SubjectClusterRow) => (
                        <span>
                          <Text code>{row.normalized_subject}</Text>
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                            {row.sample_subject ? ` · ${row.sample_subject}` : ''}
                          </Text>
                        </span>
                      ),
                    },
                    { title: 'Tickets', dataIndex: 'ticket_count', key: 'ticket_count', width: 90 },
                  ]}
                />
              ),
            }))}
          />
        ) : filteredSubjectClusterRows.length === 0 ? (
          <Text type="secondary">No rows match the current filter.</Text>
        ) : (
          <Table<SubjectClusterRow>
            size="small"
            rowKey={(r) => `${r.business_id}-${r.normalized_subject}`}
            dataSource={filteredSubjectClusterRows}
            pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
            scroll={{ x: 960 }}
            columns={[
              { title: 'Business', dataIndex: 'business_name', key: 'business_name', width: 200, ellipsis: true },
              {
                title: 'Keyword',
                dataIndex: 'normalized_subject',
                key: 'normalized_subject',
                width: 140,
                render: (kw: string) => <Text code>{kw}</Text>,
              },
              {
                title: 'Sample subject',
                dataIndex: 'sample_subject',
                key: 'sample_subject',
                ellipsis: true,
              },
              {
                title: 'Tickets',
                dataIndex: 'ticket_count',
                key: 'ticket_count',
                width: 90,
                sorter: (a, b) => a.ticket_count - b.ticket_count,
              },
              {
                title: 'Actions',
                key: 'subj_actions',
                width: 280,
                render: (_: unknown, row: SubjectClusterRow) => (
                  <Space size="small" wrap>
                    <Button type="link" size="small" onClick={() => setSubjectClusterDrawer(row)}>
                      View tickets
                    </Button>
                    <Button type="link" size="small" onClick={() => navigate(`/businesses/${row.business_id}`)}>
                      Open business
                    </Button>
                    <Button type="link" size="small" onClick={() => openCreateFromSubjectCluster(row)}>
                      New problem…
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card
        title={
          <Space>
            <FileSearchOutlined />
            <span>Repeat custom fields (same business)</span>
          </Space>
        }
        extra={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Filter business / field / value"
              onChange={(e) => setRepeatCfSearch(e.target.value)}
              style={{ width: 260 }}
            />
            <Button size="small" onClick={() => refetchCfClusters()}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Groups tickets by <strong>business + custom field + value</strong> in the last 14 days (same scope as
          Patterns). Use <Text strong>New problem…</Text> to set <Text code>signature_field_key</Text> /{' '}
          <Text code>issue_value</Text> and bulk-link tickets.
        </Text>
        {cfClusterLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : cfClusterFlatRows.length === 0 ? (
          <Text type="secondary">No repeat custom-field clusters in this window (or custom fields not in use).</Text>
        ) : filteredCfClusterRows.length === 0 ? (
          <Text type="secondary">No rows match the current filter.</Text>
        ) : (
          <Table<CfClusterRow>
            size="small"
            rowKey={(r) => `${r.business_id}-${r.field_key}-${r.issue_value}`}
            dataSource={filteredCfClusterRows}
            pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
            scroll={{ x: 1040 }}
            columns={[
              { title: 'Business', dataIndex: 'business_name', key: 'business_name', width: 200, ellipsis: true },
              {
                title: 'Field',
                key: 'field',
                width: 180,
                ellipsis: true,
                render: (_: unknown, row: CfClusterRow) => (
                  <span>
                    <Text>{row.field_label || row.field_key}</Text>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                      {row.field_key}
                    </Text>
                  </span>
                ),
              },
              { title: 'Value', dataIndex: 'issue_value', key: 'issue_value', ellipsis: true },
              {
                title: 'Tickets',
                dataIndex: 'ticket_count',
                key: 'ticket_count',
                width: 90,
                sorter: (a, b) => a.ticket_count - b.ticket_count,
              },
              {
                title: 'Actions',
                key: 'cf_actions',
                width: 280,
                render: (_: unknown, row: CfClusterRow) => (
                  <Space size="small" wrap>
                    <Button type="link" size="small" onClick={() => setCfClusterDrawer(row)}>
                      View tickets
                    </Button>
                    <Button type="link" size="small" onClick={() => navigate(`/businesses/${row.business_id}`)}>
                      Open business
                    </Button>
                    <Button type="link" size="small" onClick={() => openCreateFromCfCluster(row)}>
                      New problem…
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>
    </>
  );

  const semanticTab = (
    <>
      <Card
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <BarChartOutlined />
            <span>Volume by category (recent)</span>
          </Space>
        }
        extra={
          <Button size="small" onClick={() => refetchVolumeSpikes()}>
            Refresh
          </Button>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Ticket counts in the last 14 days, grouped by category (scoped to tickets you can see). Use this to spot
          where workload concentrates before drilling into semantic clusters.
        </Text>
        {volumeSpikesLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : !(volumeSpikesData?.by_category as VolumeCategoryRow[] | undefined)?.length ? (
          <Text type="secondary">No data in this window.</Text>
        ) : (
          <Table<VolumeCategoryRow>
            size="small"
            rowKey={(r) => r.category_id || r.category_name}
            pagination={false}
            dataSource={volumeSpikesData.by_category as VolumeCategoryRow[]}
            columns={[
              { title: 'Category', dataIndex: 'category_name', key: 'category_name', ellipsis: true },
              { title: 'Tickets', dataIndex: 'ticket_count', key: 'ticket_count', width: 100 },
            ]}
          />
        )}
      </Card>

      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            <span>Semantic groups (description + comments)</span>
          </Space>
        }
        extra={
          <Button size="small" onClick={() => refetchSemantic()}>
            Refresh
          </Button>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Groups of tickets in the same business that look similar in embedding space (subject, description, and
          comments). Requires an embedding provider on the API server (<Text code>OPENAI_API_KEY</Text> or{' '}
          <Text code>OLLAMA_BASE_URL</Text> + models) and stored embeddings (create/update tickets or run the backfill
          script). Suggested threshold is conservative; use alongside the Patterns tab.
        </Text>
        {semanticLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : semanticData?.embeddingConfigured === false ? (
          <Text type="secondary">
            Embeddings are not configured (missing API key or empty index). Similar groups will appear after setup.
          </Text>
        ) : semanticClustersByBusiness.length === 0 ? (
          <Text type="secondary">No semantic clusters met the threshold in this window.</Text>
        ) : (
          <Collapse
            bordered={false}
            style={{ background: 'transparent' }}
            items={semanticClustersByBusiness.map((grp) => ({
              key: `sem-${grp.business_id}`,
              label: (
                <Space wrap>
                  <Text strong>{grp.business_name}</Text>
                  <Tag>{grp.clusters.length} group{grp.clusters.length === 1 ? '' : 's'}</Tag>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/businesses/${grp.business_id}`);
                    }}
                  >
                    Open business
                  </Button>
                </Space>
              ),
              children: (
                <Table<SemanticClusterRow>
                  size="small"
                  rowKey={(r) => `${grp.business_id}-${r.exemplar_ticket_number}-${r.ticket_count}`}
                  pagination={false}
                  dataSource={grp.clusters}
                  expandable={{
                    expandedRowRender: (row) => (
                      <Table<(ProactiveTicket & { id?: string })>
                        size="small"
                        rowKey={(t) => t.ticket_number}
                        dataSource={row.tickets}
                        pagination={false}
                        columns={[
                          {
                            title: 'Ticket',
                            dataIndex: 'ticket_number',
                            key: 'ticket_number',
                            width: 110,
                            render: (n: string) => (
                              <Button type="link" size="small" onClick={() => navigate(`/workspace/number/${n}`)}>
                                #{n}
                              </Button>
                            ),
                          },
                          { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
                          {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            width: 90,
                            render: (s: string) => <Tag>{s}</Tag>,
                          },
                          {
                            title: 'Created',
                            dataIndex: 'created_at',
                            key: 'created_at',
                            width: 150,
                            render: (d: string) => dayjs(d).format('MMM D, YYYY HH:mm'),
                          },
                        ]}
                      />
                    ),
                  }}
                  columns={[
                    {
                      title: 'Exemplar / theme',
                      key: 'ex',
                      ellipsis: true,
                      render: (_: unknown, row: SemanticClusterRow) => (
                        <span>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            #{row.exemplar_ticket_number}
                          </Text>
                          <Text style={{ marginLeft: 8 }}>{row.exemplar_subject || '—'}</Text>
                        </span>
                      ),
                    },
                    { title: 'Tickets', dataIndex: 'ticket_count', key: 'ticket_count', width: 80 },
                    {
                      title: 'Actions',
                      key: 'act',
                      width: 200,
                      render: (_: unknown, row: SemanticClusterRow) => (
                        <Space size="small" wrap>
                          <Button
                            type="link"
                            size="small"
                            onClick={() => navigate(`/workspace/number/${row.exemplar_ticket_number}`)}
                          >
                            Open exemplar
                          </Button>
                          <Button
                            type="link"
                            size="small"
                            onClick={() => {
                              setCreateAttach({});
                              form.setFieldsValue({
                                title: (row.exemplar_subject || `Related tickets (${row.ticket_count})`).slice(
                                  0,
                                  500
                                ),
                                business_ids: [grp.business_id],
                              });
                              setExtraBusinessChoices([{ id: grp.business_id, business_name: grp.business_name }]);
                              setBusinessSearch('');
                              setCreateOpen(true);
                            }}
                          >
                            New problem…
                          </Button>
                        </Space>
                      ),
                    },
                  ]}
                />
              ),
            }))}
          />
        )}
      </Card>
    </>
  );

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} align="center">
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Problems
          </Title>
          <Text type="secondary">
            Track underlying causes for recurring incidents. Link related tickets from the drawer.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setCreateAttach({});
            setBusinessSearch('');
            setExtraBusinessChoices([]);
            setCreateOpen(true);
          }}
        >
          New problem
        </Button>
      </Space>

      <Tabs
        defaultActiveKey="tickets"
        items={[
          { key: 'tickets', label: 'Problem Tickets', children: problemTicketsTab },
          { key: 'patterns', label: 'Patterns', children: patternsTab },
          { key: 'semantic', label: 'Semantic groups', children: semanticTab },
        ]}
      />

      <Modal
        title="New problem"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          setCreateAttach({});
          form.resetFields();
          setBusinessSearch('');
          setExtraBusinessChoices([]);
        }}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isLoading}
        width={560}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => createMutation.mutate({ values, attach: { ...createAttach } })}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Short title for the underlying issue" />
          </Form.Item>
          <Form.Item
            name="business_ids"
            label="Businesses"
            rules={[
              {
                validator: async (_, v) => {
                  if (Array.isArray(v) && v.length > 0) return;
                  throw new Error('Select at least one business');
                },
              },
            ]}
          >
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Search and select one or more businesses"
              filterOption={false}
              onSearch={(v) => setBusinessSearch(v)}
              maxTagCount="responsive"
              dropdownStyle={{ minWidth: 420 }}
              dropdownMatchSelectWidth={false}
              notFoundContent={
                businessSearch.length < 1 ? (
                  'Type to filter, or pick from the list below'
                ) : businessesSearchLoading ? (
                  <Spin size="small" />
                ) : (
                  'No businesses found'
                )
              }
              suffixIcon={businessesSearchLoading ? <Spin size="small" /> : undefined}
              options={mergedBusinessOptions.map((b: BusinessSearchRow) => ({
                value: b.id,
                label: b.business_name,
              }))}
              optionFilterProp="label"
              optionRender={renderCreateBusinessOption}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Context, scope, affected businesses" />
          </Form.Item>
          <Form.Item name="root_cause" label="Root cause (when known)">
            <Input.TextArea rows={2} placeholder="Permanent fix or vendor dependency" />
          </Form.Item>
          <Form.Item name="owner_user_id" label="Owner">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Assign owner"
              options={(assignable || []).map((u: { id: string; first_name?: string; last_name?: string }) => ({
                value: u.id,
                label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="open">
            <Select
              options={[
                { value: 'open', label: 'Open' },
                { value: 'in_progress', label: 'In progress' },
                { value: 'resolved', label: 'Resolved' },
              ]}
            />
          </Form.Item>
          <Form.Item name="target_date" label="Target date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="signature_field_key" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="category_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="issue_value" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={
          subjectClusterDrawer ? (
            <Space direction="vertical" size={0}>
              <Text strong>Tickets — {subjectClusterDrawer.business_name}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Keyword <Text code>{subjectClusterDrawer.normalized_subject}</Text> · {subjectClusterDrawer.ticket_count}{' '}
                tickets
              </Text>
            </Space>
          ) : (
            'Cluster'
          )
        }
        width={640}
        open={!!subjectClusterDrawer}
        onClose={() => setSubjectClusterDrawer(null)}
        extra={
          subjectClusterDrawer ? (
            <Space>
              <Button type="primary" size="small" onClick={() => openCreateFromSubjectCluster(subjectClusterDrawer)}>
                New problem…
              </Button>
            </Space>
          ) : null
        }
      >
        {subjectClusterDrawer ? (
          <Table<ProactiveTicket>
            size="small"
            rowKey="ticket_number"
            dataSource={subjectClusterDrawer.tickets}
            pagination={false}
            columns={proactiveTicketTableColumns}
          />
        ) : null}
      </Drawer>

      <Drawer
        title={
          cfClusterDrawer ? (
            <Space direction="vertical" size={0}>
              <Text strong>Tickets — {cfClusterDrawer.business_name}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {cfClusterDrawer.field_label || cfClusterDrawer.field_key} = {cfClusterDrawer.issue_value} ·{' '}
                {cfClusterDrawer.ticket_count} tickets
              </Text>
            </Space>
          ) : (
            'Cluster'
          )
        }
        width={640}
        open={!!cfClusterDrawer}
        onClose={() => setCfClusterDrawer(null)}
        extra={
          cfClusterDrawer ? (
            <Space>
              <Button type="primary" size="small" onClick={() => openCreateFromCfCluster(cfClusterDrawer)}>
                New problem…
              </Button>
            </Space>
          ) : null
        }
      >
        {cfClusterDrawer ? (
          <Table<ProactiveTicket>
            size="small"
            rowKey="ticket_number"
            dataSource={cfClusterDrawer.tickets}
            pagination={false}
            columns={proactiveTicketTableColumns}
          />
        ) : null}
      </Drawer>

      <Drawer
        title={detailData?.problem?.title || 'Problem'}
        width={720}
        open={!!detailId}
        onClose={() => {
          setDetailId(null);
          setBusinessSearch('');
        }}
        extra={
          <Space>
            <Button
              icon={<LinkOutlined />}
              onClick={() => {
                setLinkTicketOpen(true);
              }}
            >
              Link ticket
            </Button>
            {detailData?.problem?.id ? (
              <Popconfirm
                title="Delete this problem?"
                description="Removes the record and ticket links. This cannot be undone."
                okText="Delete"
                okButtonProps={{ danger: true, loading: deleteProblemMutation.isLoading }}
                cancelText="Cancel"
                onConfirm={() => deleteProblemMutation.mutate(detailData.problem.id)}
              >
                <Button danger loading={deleteProblemMutation.isLoading}>
                  Delete
                </Button>
              </Popconfirm>
            ) : null}
          </Space>
        }
      >
        {detailLoading || !detailData ? (
          <Spin />
        ) : (
          <>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text type="secondary">Businesses</Text>
                <Select
                  mode="multiple"
                  style={{ width: '100%', marginTop: 4 }}
                  showSearch
                  placeholder="Search and select one or more businesses"
                  filterOption={false}
                  onSearch={(v) => setBusinessSearch(v)}
                  loading={businessesSearchLoading}
                  maxTagCount="responsive"
                  dropdownStyle={{ minWidth: 420 }}
                  dropdownMatchSelectWidth={false}
                  value={
                    Array.isArray(detailData.problem.businesses)
                      ? detailData.problem.businesses.map((b: ProblemBusinessRef) => b.id)
                      : []
                  }
                  onChange={(business_ids) => {
                    if (!business_ids || business_ids.length === 0) {
                      message.warning('At least one business is required');
                      return;
                    }
                    updateMutation.mutate({
                      id: detailData.problem.id,
                      values: { business_ids },
                    });
                  }}
                  options={mergedBusinessOptionsDetail.map((b: BusinessSearchRow) => ({
                    value: b.id,
                    label: b.business_name,
                  }))}
                  optionFilterProp="label"
                  optionRender={renderDetailBusinessOption}
                  notFoundContent={
                    businessSearch.length < 1 ? (
                      'Type to filter, or pick from the list below'
                    ) : businessesSearchLoading ? (
                      <Spin size="small" />
                    ) : (
                      'No businesses found'
                    )
                  }
                />
              </div>
              <div>
                <Text type="secondary">Status</Text>
                <div>
                  <Select
                    style={{ minWidth: 160 }}
                    value={detailData.problem.status}
                    onChange={(status) =>
                      updateMutation.mutate({ id: detailData.problem.id, values: { status } })
                    }
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: 'in_progress', label: 'In progress' },
                      { value: 'resolved', label: 'Resolved' },
                    ]}
                  />
                </div>
              </div>
              <div>
                <Text type="secondary">Description</Text>
                <Input.TextArea
                  rows={3}
                  defaultValue={detailData.problem.description || ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== (detailData.problem.description || '')) {
                      updateMutation.mutate({ id: detailData.problem.id, values: { description: v } });
                    }
                  }}
                />
              </div>
              <div>
                <Text type="secondary">Root cause</Text>
                <Input.TextArea
                  rows={2}
                  defaultValue={detailData.problem.root_cause || ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== (detailData.problem.root_cause || '')) {
                      updateMutation.mutate({ id: detailData.problem.id, values: { root_cause: v } });
                    }
                  }}
                />
              </div>
            </Space>

            <Title level={5} style={{ marginTop: 24 }}>
              Linked tickets
            </Title>
            <Table
              size="small"
              rowKey="id"
              dataSource={detailData.tickets || []}
              columns={[
                {
                  title: 'Ticket',
                  dataIndex: 'ticket_number',
                  key: 'ticket_number',
                  render: (n: string) => (
                    <Text
                      code
                      style={{ cursor: 'pointer', color: '#1890ff' }}
                      onClick={() => navigate(`/workspace/number/${n}`)}
                    >
                      {n}
                    </Text>
                  ),
                },
                { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
                {
                  title: '',
                  key: 'x',
                  width: 80,
                  render: (_: unknown, row: { id: string }) => (
                    <Button
                      type="link"
                      danger
                      size="small"
                      onClick={() =>
                        unlinkMutation.mutate({ problemId: detailData.problem.id, ticketId: row.id })
                      }
                    >
                      Remove
                    </Button>
                  ),
                },
              ]}
              pagination={false}
            />
          </>
        )}
      </Drawer>

      <Modal
        title="Link ticket by number"
        open={linkTicketOpen}
        onCancel={() => {
          setLinkTicketOpen(false);
          linkForm.resetFields();
        }}
        onOk={() => linkForm.submit()}
        confirmLoading={linkMutation.isLoading}
      >
        <Form
          form={linkForm}
          layout="vertical"
          onFinish={(values) => {
            if (detailId && values.ticket_number) {
              linkMutation.mutate({ problemId: detailId, ticketNumber: values.ticket_number });
            }
          }}
        >
          <Form.Item
            name="ticket_number"
            label="Ticket #"
            rules={[{ required: true, message: 'Enter ticket number' }]}
          >
            <Input placeholder="e.g. 12345" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Problems;
